import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { IconAngleDown, IconPenFilled } from '@iwoca/icons';
import { Accordion, Divider, Flex, IwocaLogo, Stack, Text } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { LoanSummaryRow } from './LoanSummaryRow';
import { TrustPilot } from '../../../../../../../TrustPilot/TrustPilot';
import { Rate, VariableRate } from '../../../../../../../ui/rate';
import { NeedHelpSection } from '../../../../../components/NeedHelpSection/NeedHelpSection';
import { useLoanSummary } from '../../hooks/useLoanSummary';

type DesktopLoanSummaryProps = {
    formalOffer: TFormalOffer;
    drawdownAmount: number;
};

export const DesktopLoanSummary = ({
    formalOffer,
    drawdownAmount
}: DesktopLoanSummaryProps) => {
    const {
        monthlyRepayment,
        interestRate,
        withdrawalFeePercentage,
        fullTermMonths
    } = useLoanSummary(formalOffer, drawdownAmount);

    return (
        <>
            <Flex align="center" justify="between">
                <Text className="text-2xl font-bold">
                    <FormattedMessage
                        id="_.funding_requirements.loan_summary.title"
                        defaultMessage="Your loan summary"
                    />
                </Text>
                <Link
                    to={maintainStateKey({ pathname: '/my-offers/' })}
                    aria-label="Edit loan"
                    className="flex h-[32px] w-[32px] items-center justify-center rounded-full border border-structure-100"
                >
                    <IconPenFilled className="h-l w-l" />
                </Link>
            </Flex>

            <Stack gap="none">
                <Text className="text-m font-reg">
                    <FormattedMessage
                        id="_.funding_requirements.loan_summary.min_monthly_repayment"
                        defaultMessage="Min. monthly repayment"
                    />
                </Text>
                <Text className="text-m font-med">
                    <FormattedMessage
                        id="_.funding_requirements.loan_summary.monthly_amount"
                        defaultMessage="{amount} per month"
                        values={{
                            amount: (
                                <span className="text-3xl font-bold">
                                    <FormattedCurrency
                                        value={monthlyRepayment}
                                    />
                                </span>
                            )
                        }}
                    />
                </Text>
            </Stack>

            <Stack gap="comfortable">
                {/* ORION-113-GAP token: no surface for the denim panel, so the
                    divider can't pick up an on-dark hairline */}
                <Divider className="border-secondary-20" />
                <Stack gap="tight">
                    <LoanSummaryRow
                        label={
                            <FormattedMessage
                                id="_.loan_summary.withdrawal_amount"
                                defaultMessage="Withdrawal amount:"
                            />
                        }
                        value={<FormattedCurrency value={drawdownAmount} />}
                    />
                    <LoanSummaryRow
                        label={
                            <FormattedMessage
                                id="_.loan_summary.interest_rate"
                                defaultMessage="Interest rate /30 days:"
                            />
                        }
                        value={<Rate value={interestRate} />}
                    />
                    <LoanSummaryRow
                        label={
                            <FormattedMessage
                                id="_.loan_summary.withdrawal_fees"
                                defaultMessage="Withdrawal fees:"
                            />
                        }
                        value={
                            <VariableRate
                                minimumFractionDigits={0}
                                maximumFractionDigits={2}
                                value={withdrawalFeePercentage}
                            />
                        }
                    />
                </Stack>
                <LoanSummaryRow
                    label={
                        <FormattedMessage
                            id="_.loan_summary.full_term_duration"
                            defaultMessage="Full term duration:"
                        />
                    }
                    value={
                        <FormattedMessage
                            id="_.loan_summary.full_term_months"
                            defaultMessage="{months} months"
                            values={{ months: fullTermMonths }}
                        />
                    }
                />
            </Stack>

            {/* ORION-113-GAP component: no negative margin, so the plain
                accordion's padded header is pulled in by hand to line up with
                the text */}
            {/* ORION-113-GAP token: the 60px above the accordion is the
                section gap plus 24px, not a gap token */}
            <div className="-ml-m mt-xl">
                <Accordion.Item variant="plain" size="small">
                    {({ open }) => (
                        <>
                            <Accordion.Header>
                                <Accordion.Title className="text-s font-bold">
                                    <FormattedMessage
                                        id="_.funding_requirements.loan_summary.still_need_help"
                                        defaultMessage="Still need help?"
                                    />
                                </Accordion.Title>
                                <IconAngleDown
                                    className={classNames(
                                        'text-l transition-transform',
                                        open && 'rotate-180'
                                    )}
                                />
                            </Accordion.Header>
                            <Accordion.Content>
                                <NeedHelpSection variant="funding-desktop" />
                            </Accordion.Content>
                        </>
                    )}
                </Accordion.Item>
            </div>

            {/* Pushes the logo to the bottom of the panel */}
            <Flex.Item grow />
            {/* ORION-113-GAP off-system: text alignment, not layout */}
            <Stack gap="comfortable" align="center" className="text-center">
                <IwocaLogo className="w-[100px]" color="white" />
                <TrustPilot theme="dark" />
            </Stack>
        </>
    );
};
